import type { ReactNode } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import {
  DailyTrendPoint,
  EmployeeStats,
  MonthlyTrendPoint,
} from "../../store/allocation/employeeDashboardSlice";
import { DASHBOARD, DashboardView } from "./dashboardTheme";

const ChartBox = ({ children }: { children: ReactNode }) => (
  <div className="w-full h-full min-w-0 overflow-hidden">
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

export const ChartCard = ({
  title,
  children,
  className = "",
  height = "h-64",
}: {
  title: string;
  children: ReactNode;
  className?: string;
  height?: string;
}) => (
  <div
    className={`bg-white rounded-2xl shadow-sm p-5 min-w-0 overflow-hidden ${className}`}
    style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
  >
    <h3
      className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-4 truncate"
      style={{ color: DASHBOARD.textMuted }}
    >
      {title}
    </h3>
    <div className={`w-full min-w-0 overflow-hidden ${height}`}>{children}</div>
  </div>
);

const EmptyChart = ({ label = "No data yet" }: { label?: string }) => (
  <div className="h-full flex items-center justify-center text-sm" style={{ color: DASHBOARD.textMuted }}>
    {label}
  </div>
);

const legendStyle = { fontSize: 11, color: DASHBOARD.textMuted };

export function SemiGauge({
  value,
  color,
}: {
  value: number;
  color: string;
}) {
  const pct = Math.min(100, Math.max(0, value));
  const r = 58;
  const cx = 70;
  const cy = 72;
  const startX = cx - r;
  const endX = cx + r;
  const arcLen = Math.PI * r;
  const dashOffset = arcLen - (pct / 100) * arcLen;

  return (
    <svg viewBox="0 0 140 88" className="w-[100px] sm:w-[130px] h-[72px] sm:h-[88px] shrink-0 max-w-[45%]">
      <path
        d={`M ${startX} ${cy} A ${r} ${r} 0 0 1 ${endX} ${cy}`}
        fill="none"
        stroke={DASHBOARD.grid}
        strokeWidth="11"
        strokeLinecap="round"
      />
      <path
        d={`M ${startX} ${cy} A ${r} ${r} 0 0 1 ${endX} ${cy}`}
        fill="none"
        stroke={color}
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={arcLen}
        strokeDashoffset={dashOffset}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
    </svg>
  );
}

export function EfficiencyCard({
  title,
  value,
  color,
  footer,
}: {
  title: string;
  value: number;
  color: string;
  footer: [string, string];
}) {
  return (
    <div
      className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 flex flex-col min-w-0 overflow-hidden"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
    >
      <h3
        className="text-[11px] uppercase tracking-[0.12em] font-semibold text-center mb-3 truncate"
        style={{ color }}
      >
        {title}
      </h3>
      <div className="flex items-center justify-center gap-1 sm:gap-2 flex-1 min-w-0">
        <SemiGauge value={value} color={color} />
        <span
          className="text-2xl sm:text-3xl md:text-4xl font-bold leading-none shrink-0"
          style={{ color: DASHBOARD.textDark }}
        >
          {value}%
        </span>
      </div>
      <div
        className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-xs sm:text-sm text-center"
        style={{ color: DASHBOARD.textMuted }}
      >
        <span>{footer[0]}</span>
        <span>{footer[1]}</span>
      </div>
    </div>
  );
}

export function SummaryStatCard({
  label,
  value,
  bg,
  icon,
}: {
  label: string;
  value: number;
  bg: string;
  icon: ReactNode;
}) {
  return (
    <div
      className="rounded-2xl p-4 sm:p-5 flex flex-col justify-between min-h-[110px] relative overflow-hidden min-w-0"
      style={{ backgroundColor: bg }}
    >
      <div className="absolute top-4 right-4 opacity-70 text-white text-lg">
        {icon}
      </div>
      <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.14em] font-semibold text-white/90 pr-8 leading-tight">
        {label}
      </span>
      <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mt-2 truncate">{value}</span>
    </div>
  );
}

export function EightUnitTypesGrid({ data }: { data: DashboardView }) {
  const items = [
    { label: "Design live", value: data.designUnitsLive, color: DASHBOARD.purple },
    { label: "QC2 live", value: data.qcUnitsLive, color: DASHBOARD.teal },
    { label: "Design redesign", value: data.designUnitsRedesign, color: DASHBOARD.terracotta },
    { label: "QC2 redesign", value: data.qcUnitsRedesign, color: DASHBOARD.darkTeal },
    { label: "Design internal live", value: data.designUnitsInternalLive, color: DASHBOARD.internalLive },
    { label: "QC2 internal live", value: data.qcUnitsInternalLive, color: DASHBOARD.lightPurple },
    { label: "Design internal RD", value: data.designUnitsInternalRedesign, color: DASHBOARD.internalRd },
    { label: "QC2 internal RD", value: data.qcUnitsInternalRedesign, color: "#8B6B5A" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 [&>*]:min-w-0">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-2xl bg-white px-4 py-3 min-w-0 overflow-hidden"
          style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
        >
          <div
            className="text-[11px] uppercase tracking-[0.08em] font-semibold truncate"
            style={{ color: item.color }}
          >
            {item.label}
          </div>
          <div
            className="text-2xl sm:text-3xl font-bold mt-1 tabular-nums"
            style={{ color: DASHBOARD.textDark }}
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DesignQcRedesignMix({ data }: { data: DashboardView }) {
  const chartData = [
    { name: "Design live", value: data.designUnitsLive, color: DASHBOARD.purple },
    { name: "QC2 live", value: data.qcUnitsLive, color: DASHBOARD.teal },
    { name: "Design redesign", value: data.designUnitsRedesign, color: DASHBOARD.terracotta },
    { name: "QC2 redesign", value: data.qcUnitsRedesign, color: DASHBOARD.darkTeal },
    { name: "Design internal live", value: data.designUnitsInternalLive, color: DASHBOARD.internalLive },
    { name: "QC2 internal live", value: data.qcUnitsInternalLive, color: DASHBOARD.lightPurple },
    { name: "Design internal RD", value: data.designUnitsInternalRedesign, color: DASHBOARD.internalRd },
    { name: "QC2 internal RD", value: data.qcUnitsInternalRedesign, color: "#8B6B5A" },
  ].filter((d) => d.value > 0);

  if (!chartData.length) return <EmptyChart />;

  return (
    <ChartBox>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="48%"
          innerRadius="46%"
          outerRadius="72%"
          paddingAngle={2}
          stroke="none"
        >
          {chartData.map((entry) => (
            <Cell key={entry.name} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          iconSize={8}
          wrapperStyle={legendStyle}
        />
      </PieChart>
    </ChartBox>
  );
}

export function UnitsPeriodBar({ data }: { data: DashboardView }) {
  const chartData = [
    { name: "Day", units: data.unitsDay, fill: DASHBOARD.purple },
    { name: "Week", units: data.unitsWeek, fill: DASHBOARD.teal },
    { name: "Month", units: data.unitsMonth, fill: DASHBOARD.darkTeal },
  ];
  const maxVal = Math.max(...chartData.map((d) => d.units), 1);
  const yMax = Math.ceil(maxVal / 20) * 20 || 20;
  const yTicks = Array.from({ length: yMax / 20 + 1 }, (_, i) => i * 20);

  return (
    <ChartBox>
      <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid stroke={DASHBOARD.grid} strokeDasharray="0" vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: DASHBOARD.textMuted }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: DASHBOARD.textMuted }} axisLine={false} tickLine={false} domain={[0, yMax]} ticks={yTicks} />
        <Tooltip />
        <Bar dataKey="units" name="Units" radius={[8, 8, 0, 0]}>
          {chartData.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ChartBox>
  );
}

export function ReworkRateDonut({ data }: { data: DashboardView }) {
  const chartData = [
    { name: "Delivered clean", value: data.reworkClean },
    { name: "Returned for redesign", value: data.reworkReturned },
  ].filter((d) => d.value > 0);

  if (!chartData.length) return <EmptyChart />;

  return (
    <ChartBox>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="48%"
          innerRadius="52%"
          outerRadius="72%"
          paddingAngle={2}
          stroke="none"
        >
          <Cell fill={DASHBOARD.teal} />
          <Cell fill={DASHBOARD.terracotta} />
        </Pie>
        <Tooltip />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          iconSize={8}
          wrapperStyle={legendStyle}
        />
      </PieChart>
    </ChartBox>
  );
}

export function LiveVsRedesignBar({ data }: { data: DashboardView }) {
  const chartData = [
    {
      name: "Live",
      design: data.designUnitsLive,
      qc: data.qcUnitsLive,
    },
    {
      name: "Redesign",
      design: data.designUnitsRedesign,
      qc: data.qcUnitsRedesign,
    },
    {
      name: "Internal live",
      design: data.designUnitsInternalLive,
      qc: data.qcUnitsInternalLive,
    },
    {
      name: "Internal RD",
      design: data.designUnitsInternalRedesign,
      qc: data.qcUnitsInternalRedesign,
    },
  ];
  const maxVal = Math.max(
    ...chartData.map((d) => d.design + d.qc),
    1,
  );
  const yMax = Math.ceil(maxVal / 20) * 20 || 20;
  const yTicks = Array.from({ length: yMax / 20 + 1 }, (_, i) => i * 20);

  if (!chartData.some((d) => d.design || d.qc)) return <EmptyChart />;

  return (
    <ChartBox>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
      >
        <CartesianGrid stroke={DASHBOARD.grid} strokeDasharray="0" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11, fill: DASHBOARD.textMuted }} axisLine={false} tickLine={false} domain={[0, yMax]} ticks={yTicks} />
        <YAxis
          type="category"
          dataKey="name"
          width={88}
          tick={{ fontSize: 10, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip />
        <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
        <Bar dataKey="design" name="Design" stackId="a" fill={DASHBOARD.purple} barSize={22} />
        <Bar dataKey="qc" name="QC2" stackId="a" fill={DASHBOARD.teal} radius={[0, 8, 8, 0]} barSize={22} />
      </BarChart>
    </ChartBox>
  );
}

export function PipelineFlow({ data }: { data: DashboardView }) {
  const blocks = [
    { value: data.pipelineTotal, label: "TOTAL", bg: DASHBOARD.purple },
    { value: data.pipelineDesign, label: "DESIGN", bg: DASHBOARD.lightPurple },
    { value: data.pipelineQc, label: "QC", bg: DASHBOARD.teal },
    { value: data.pipelineDelivered, label: "DELIVERED", bg: DASHBOARD.darkTeal },
  ];

  return (
    <div className="min-w-0 overflow-hidden">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 [&>*]:min-w-0">
        {blocks.map((b) => (
          <div
            key={b.label}
            className="rounded-2xl py-6 sm:py-8 px-3 sm:px-4 text-center text-white min-w-0 overflow-hidden"
            style={{ backgroundColor: b.bg }}
          >
            <div className="text-2xl sm:text-4xl md:text-5xl font-bold truncate">{b.value}</div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.14em] mt-2 font-semibold opacity-90">
              {b.label}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-start gap-2 mt-4 text-sm min-w-0" style={{ color: DASHBOARD.textMuted }}>
        <span
          className="w-2 h-2 rounded-full shrink-0 mt-1.5"
          style={{ backgroundColor: DASHBOARD.terracotta }}
        />
        <span className="min-w-0 break-words">
          {data.pipelineBranchUnits} units ({data.pipelineBranchCases} cases) branched into
          Redesign before final delivery
        </span>
      </div>
    </div>
  );
}

export function CasesCapacityBar({ data }: { data: DashboardView }) {
  const chartData = [
    { name: "Cases", value: data.casesCapacity.casesCredited },
    { name: "QC Cases", value: data.casesCapacity.qcCases },
    { name: "Total Units", value: data.casesCapacity.totalUnits },
    { name: "Design", value: data.casesCapacity.designUnits },
    { name: "QC Units", value: data.casesCapacity.qcUnits },
    { name: "Period Cap.", value: data.casesCapacity.capacity },
  ];
  const maxVal = Math.max(...chartData.map((d) => d.value), 1);
  const yMax = Math.ceil(maxVal / 20) * 20 || 20;
  const yTicks = Array.from({ length: yMax / 20 + 1 }, (_, i) => i * 20);

  return (
    <ChartBox>
      <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid stroke={DASHBOARD.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 11, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
          domain={[0, yMax]}
          ticks={yTicks}
        />
        <Tooltip />
        <Bar dataKey="value" fill={DASHBOARD.purple} radius={[8, 8, 0, 0]} />
      </BarChart>
    </ChartBox>
  );
}

export function DailyUnitsTrend({ trend }: { trend?: DailyTrendPoint[] }) {
  const chartData =
    trend?.map((d) => ({
      label: d.label,
      design: d.design,
      qc: d.qc,
      redesign: d.redesign ?? 0,
    })) ?? [];

  const hasData = chartData.some((d) => d.design || d.qc || d.redesign);
  if (!hasData) return <EmptyChart label="No daily activity in selected range" />;

  const maxVal = Math.max(
    ...chartData.map((d) => Math.max(d.design, d.qc, d.redesign)),
    1
  );
  const yMax = Math.ceil(maxVal / 4) * 4 || 4;
  const yTicks = Array.from({ length: yMax / 4 + 1 }, (_, i) => i * 4);

  return renderDailyChart(chartData, yMax, yTicks);
}

function renderDailyChart(
  chartData: { label: string; design: number; qc: number; redesign: number }[],
  yMax = 16,
  yTicks = [0, 4, 8, 12, 16]
) {
  return (
    <ChartBox>
      <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid stroke={DASHBOARD.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 9, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
          angle={-45}
          textAnchor="end"
          height={50}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 11, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
          domain={[0, yMax]}
          ticks={yTicks}
        />
        <Tooltip />
        <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
        <Area
          type="monotone"
          dataKey="design"
          name="Design"
          stroke={DASHBOARD.purple}
          fill={DASHBOARD.purple}
          fillOpacity={0.12}
          strokeWidth={2}
          dot={{ r: 3, fill: DASHBOARD.purple }}
        />
        <Area
          type="monotone"
          dataKey="qc"
          name="QC"
          stroke={DASHBOARD.teal}
          fill={DASHBOARD.teal}
          fillOpacity={0.08}
          strokeWidth={2}
          dot={{ r: 3, fill: DASHBOARD.teal }}
        />
        <Area
          type="monotone"
          dataKey="redesign"
          name="Redesign"
          stroke={DASHBOARD.terracotta}
          fill={DASHBOARD.terracotta}
          fillOpacity={0.06}
          strokeWidth={2}
          dot={{ r: 3, fill: DASHBOARD.terracotta }}
        />
      </AreaChart>
    </ChartBox>
  );
}

export function MonthlyTrendChart({ trend }: { trend?: MonthlyTrendPoint[] }) {
  const chartData =
    trend?.map((d) => ({
      label: d.label,
      design: d.design,
      qc: d.qc,
      redesign: d.redesign ?? 0,
    })) ?? [];

  const hasData = chartData.some((d) => d.design || d.qc || d.redesign);
  if (!hasData) return <EmptyChart label="No monthly activity in selected range" />;

  const maxVal = Math.max(
    ...chartData.map((d) => Math.max(d.design, d.qc, d.redesign)),
    1
  );
  const yMax = Math.ceil(maxVal / 20) * 20 || 20;
  const yTicks = Array.from({ length: yMax / 20 + 1 }, (_, i) => i * 20);

  return renderMonthlyChart(chartData, yMax, yTicks);
}

function renderMonthlyChart(
  chartData: { label: string; design: number; qc: number; redesign: number }[],
  yMax = 140,
  yTicks = [0, 20, 40, 60, 80, 100, 120, 140]
) {
  return (
    <ChartBox>
      <LineChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid stroke={DASHBOARD.grid} strokeDasharray="0" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: DASHBOARD.textMuted }}
          axisLine={false}
          tickLine={false}
          domain={[0, yMax]}
          ticks={yTicks}
        />
        <Tooltip />
        <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
        <Line
          type="monotone"
          dataKey="design"
          name="Design"
          stroke={DASHBOARD.purple}
          strokeWidth={2}
          dot={{ r: 4, fill: DASHBOARD.purple }}
        />
        <Line
          type="monotone"
          dataKey="qc"
          name="QC"
          stroke={DASHBOARD.teal}
          strokeWidth={2}
          dot={{ r: 4, fill: DASHBOARD.teal }}
        />
        <Line
          type="monotone"
          dataKey="redesign"
          name="Redesign"
          stroke={DASHBOARD.terracotta}
          strokeWidth={2}
          dot={{ r: 4, fill: DASHBOARD.terracotta }}
        />
      </LineChart>
    </ChartBox>
  );
}

/* ── Team page charts (unchanged exports) ── */

export function DesignQcPie({ stats }: { stats: EmployeeStats }) {
  const data = [
    { name: "Design", value: stats.design_units || 0 },
    { name: "QC", value: stats.qc_units || 0 },
  ].filter((d) => d.value > 0);

  if (!data.length) return <EmptyChart label="No units credited yet" />;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={3}
        >
          {data.map((entry) => (
            <Cell
              key={entry.name}
              fill={entry.name === "Design" ? DASHBOARD.purple : DASHBOARD.teal}
            />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function PeriodBar({ stats }: { stats: EmployeeStats }) {
  const data = [
    { name: "Day", units: stats.units_day || 0 },
    { name: "Week", units: stats.units_week || 0 },
    { name: "Month", units: stats.units_month || 0 },
  ];

  if (!data.some((d) => d.units > 0)) {
    return <EmptyChart label="No units in current periods" />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DASHBOARD.grid} />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="units" name="Units" radius={[6, 6, 0, 0]} fill={DASHBOARD.purple} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CasesVsUnitsBar({ stats }: { stats: EmployeeStats }) {
  const data = [
    { name: "Credited", value: stats.cases_credited || 0 },
    { name: "Total Units", value: stats.total_units || 0 },
    { name: "Design", value: stats.design_units || 0 },
    { name: "QC", value: stats.qc_units || 0 },
    { name: "Period Cap.", value: stats.period_capacity || 0 },
  ];

  if (!data.some((d) => d.value > 0)) return <EmptyChart />;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DASHBOARD.grid} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" name="Count" fill={DASHBOARD.purple} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DailyTrendArea({ trend }: { trend?: DailyTrendPoint[] }) {
  return <DailyUnitsTrend trend={trend} />;
}

export function MonthlyTrendLine({ trend }: { trend?: MonthlyTrendPoint[] }) {
  return <MonthlyTrendChart trend={trend} />;
}

const ROLE_COLORS = [
  DASHBOARD.teal,
  DASHBOARD.terracotta,
  DASHBOARD.purple,
  DASHBOARD.darkTeal,
  DASHBOARD.lightPurple,
  "#94A3B8",
  "#EF4444",
];

export function TeamUnitsBar({ team }: { team: EmployeeStats[] }) {
  const data = [...team]
    .sort((a, b) => b.total_units - a.total_units)
    .slice(0, 12)
    .map((e) => ({
      name: e.emp_name.length > 12 ? `${e.emp_name.slice(0, 12)}…` : e.emp_name,
      fullName: e.emp_name,
      live: (e.design_units_live || 0) + (e.qc_units_live || 0),
      redesign: (e.design_units_redesign || 0) + (e.qc_units_redesign || 0),
      internalLive:
        (e.design_units_internal_live || 0) + (e.qc_units_internal_live || 0),
      internalRd:
        (e.design_units_internal_redesign || 0) +
        (e.qc_units_internal_redesign || 0),
      total: e.total_units || 0,
    }));

  if (!data.length || !data.some((d) => d.total > 0)) {
    return <EmptyChart label="No team units to chart" />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DASHBOARD.grid} />
        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
        <Tooltip
          formatter={(value) => [Number(value), ""]}
          labelFormatter={(_, payload) =>
            (payload?.[0]?.payload as { fullName?: string })?.fullName || ""
          }
        />
        <Legend />
        <Bar dataKey="live" name="Live" stackId="a" fill={DASHBOARD.purple} />
        <Bar dataKey="redesign" name="Redesign" stackId="a" fill={DASHBOARD.terracotta} />
        <Bar dataKey="internalLive" name="Internal live" stackId="a" fill={DASHBOARD.internalLive} />
        <Bar dataKey="internalRd" name="Internal RD" stackId="a" fill={DASHBOARD.internalRd} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TeamRolePie({ team }: { team: EmployeeStats[] }) {
  const byRole = new Map<string, number>();
  for (const emp of team) {
    byRole.set(emp.role, (byRole.get(emp.role) || 0) + (emp.total_units || 0));
  }

  const data = [...byRole.entries()]
    .map(([name, value]) => ({ name, value }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!data.length) return <EmptyChart label="No units by role yet" />;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
          {data.map((entry, i) => (
            <Cell key={entry.name} fill={ROLE_COLORS[i % ROLE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function TeamPeriodCompare({ team }: { team: EmployeeStats[] }) {
  const data = [...team]
    .sort((a, b) => b.units_month - a.units_month)
    .slice(0, 10)
    .map((e) => ({
      name: e.emp_name.length > 10 ? `${e.emp_name.slice(0, 10)}…` : e.emp_name,
      fullName: e.emp_name,
      day: e.units_day || 0,
      week: e.units_week || 0,
      month: e.units_month || 0,
    }));

  if (!data.length || !data.some((d) => d.day || d.week || d.month)) {
    return <EmptyChart label="No period activity to compare" />;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DASHBOARD.grid} />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
        <Tooltip
          labelFormatter={(_, payload) =>
            (payload?.[0]?.payload as { fullName?: string })?.fullName || ""
          }
        />
        <Legend />
        <Bar dataKey="day" name="Day" fill={DASHBOARD.purple} radius={[4, 4, 0, 0]} />
        <Bar dataKey="week" name="Week" fill={DASHBOARD.teal} radius={[4, 4, 0, 0]} />
        <Bar dataKey="month" name="Month" fill={DASHBOARD.darkTeal} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
